import { createSlice } from '@reduxjs/toolkit';
import { getBasketData } from 'services/getBasketData';
import { FetchPaymentBasketPayload, PaymentBasketState } from 'types/baskets';
import { getStatus } from 'redux/slices/baskets/givingBasketSilce';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { selectSelectedParishId } from 'redux/selectors/parishContext';

type PaymentState = PaymentBasketState & { loadedParishId: string | null };

const initialState: PaymentState = {
    activeRecords: [],
    inActiveRecords: [],
    loading: false,
    error: null,
    loadMore: false,
    loadedParishId: null,
};

export const fetchPaymentBasket = createAsyncThunkWrapper<any, FetchPaymentBasketPayload>(
    'payment/fetchPaymentBasket',
    async ({ status, subType, headers, name, companyId, parishId }: FetchPaymentBasketPayload, { getState }) => {
        const { pageSize, pageNumber } = headers;
        const resolvedParishId = parishId || companyId || selectSelectedParishId(getState() as any);
        if (!resolvedParishId) {
            return;
        }
        const res = await getBasketData({
            status,
            subType,
            parishId: resolvedParishId,
            headers,
            name,
        });
        return { response: res, pageNumber, pageSize, status, parishId: resolvedParishId };
    }
);

const paymentBasketSlice = createSlice({
    name: 'paymentBasket',
    initialState,
    reducers: {
        resetPaymentBaskets: () => ({ ...initialState }),
    },
    extraReducers: (builder) => {
        builder.addCase(fetchPaymentBasket.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchPaymentBasket.fulfilled, (state, action) => {
            state.loading = false;
            const { payload } = action || {};
            if (!payload?.response) return;

            const { response, pageNumber, pageSize, status, parishId } = payload || {};
            const { events, loadMore, total } = response || {};
            if (pageNumber !== 0 && state.loadedParishId !== parishId) return;

            if (typeof loadMore === 'boolean') {
                state.loadMore = loadMore;
            } else {
                state.loadMore = typeof total === 'number' ? (pageNumber + 1) * pageSize < total : events?.length >= pageSize;
            }

            const keyToUpdate = getStatus(status) === 'PENDING' ? 'inActiveRecords' : 'activeRecords';
            if (pageNumber === 0) {
                state.loadedParishId = parishId;
                state.activeRecords = [];
                state.inActiveRecords = [];
                state[keyToUpdate] = events || [];
            } else {
                state[keyToUpdate] = [...(state?.[keyToUpdate] || []), ...(events || [])];
            }
        });
        builder.addCase(fetchPaymentBasket.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || null;
        });
    },
});

export const { resetPaymentBaskets } = paymentBasketSlice.actions;
export default paymentBasketSlice.reducer;
